import React, { useState } from 'react';
import inspectionService from '../../services/inspectionService';
import { useSpinner } from '../../context/SpinnerContext';
import logger from '../../utils/logger';
import './InspectionDateRangeReport.css';

const InspectionDateRangeReport = ({ onNotify }) => {
    const { showSpinner, hideSpinner } = useSpinner();
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [dateRangeError, setDateRangeError] = useState('');

    const getMaxDate = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const validateDateRange = (start, end) => {
        if (!start || !end) {
            setDateRangeError('');
            return true;
        }

        const startDateObj = new Date(start);
        const endDateObj = new Date(end);
        const today = new Date();
        today.setHours(23, 59, 59, 999);

        if (startDateObj > today || endDateObj > today) {
            setDateRangeError('Las fechas no pueden ser mayores a la fecha actual');
            return false;
        }

        if (startDateObj > endDateObj) {
            setDateRangeError('La fecha de inicio no puede ser mayor que la fecha de fin');
            return false;
        }

        const diffTime = Math.abs(endDateObj - startDateObj);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        const maxDays = 90;

        if (diffDays > maxDays) {
            setDateRangeError(`El rango de fechas no puede exceder ${maxDays} días (3 meses). Rango actual: ${diffDays} días`);
            return false;
        }

        setDateRangeError('');
        return true;
    };

    const handleStartDateChange = (event) => {
        const value = event.target.value;
        setStartDate(value);

        const isoStart = value ? `${value}T00:00:00.000Z` : '';
        const isoEnd = endDate ? `${endDate}T23:59:59.999Z` : '';

        if (value && endDate) {
            validateDateRange(isoStart, isoEnd);
        } else {
            setDateRangeError('');
        }
    };

    const handleEndDateChange = (event) => {
        const value = event.target.value;
        setEndDate(value);

        const isoStart = startDate ? `${startDate}T00:00:00.000Z` : '';
        const isoEnd = value ? `${value}T23:59:59.999Z` : '';

        if (startDate && value) {
            validateDateRange(isoStart, isoEnd);
        } else {
            setDateRangeError('');
        }
    };

    const handleGenerateReport = async (event) => {
        event.preventDefault();

        if (!startDate || !endDate) {
            onNotify('Por favor selecciona ambas fechas', 'error');
            return;
        }

        const isoStart = `${startDate}T00:00:00.000Z`;
        const isoEnd = `${endDate}T23:59:59.999Z`;

        if (!validateDateRange(isoStart, isoEnd)) {
            onNotify(dateRangeError || 'El rango de fechas no es válido', 'error');
            return;
        }

        try {
            showSpinner('Generando reporte...');
            logger.info('Generando reporte...', {
                startDate: isoStart,
                endDate: isoEnd,
            }, 'InspectionDateRangeReport');

            const response = await inspectionService.generateReport(isoStart, isoEnd);

            if (response.downloadUrl) {
                const link = document.createElement('a');
                link.href = response.downloadUrl;
                link.download = response.fileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                onNotify(
                    `Reporte generado exitosamente. Se encontraron ${response.inspectionsCount} inspecciones.`,
                    'success'
                );
            } else {
                onNotify('Error: No se recibió URL de descarga', 'error');
            }
        } catch (error) {
            logger.error('Error generating report:', error, null, 'InspectionDateRangeReport');
            onNotify(error.message || 'Error al generar el reporte', 'error');
        } finally {
            hideSpinner();
        }
    };

    return (
        <section className="report-card inspection-date-range-report">
            <h2 className="report-card-title">Generar Reporte de Inspecciones</h2>
            <p className="report-card-subtitle">
                Descarga un Excel con todas las inspecciones de un rango de fechas
            </p>

            <form onSubmit={handleGenerateReport} className="report-form">
                <div className="form-section">
                    <div className="date-input-group">
                        <div className="date-field">
                            <label htmlFor="startDate">Fecha de Inicio</label>
                            <div className="date-input-wrapper">
                                <input
                                    type="date"
                                    id="startDate"
                                    className="date-input"
                                    value={startDate}
                                    onChange={handleStartDateChange}
                                    max={getMaxDate()}
                                    required
                                />
                            </div>
                        </div>

                        <div className="date-field">
                            <label htmlFor="endDate">Fecha de Fin</label>
                            <div className="date-input-wrapper">
                                <input
                                    type="date"
                                    id="endDate"
                                    className="date-input"
                                    value={endDate}
                                    onChange={handleEndDateChange}
                                    max={getMaxDate()}
                                    required
                                />
                            </div>
                        </div>
                    </div>

                    {dateRangeError && (
                        <div className="error-message">
                            {dateRangeError}
                        </div>
                    )}

                    <div className="form-help-text">
                        <p>• El rango máximo permitido es de 3 meses (90 días)</p>
                        <p>• El reporte se descargará automáticamente al generarse</p>
                    </div>
                </div>

                <div className="report-card-actions">
                    <button
                        type="submit"
                        className="btn-generate-report btn-generate-report-primary"
                        disabled={!!dateRangeError || !startDate || !endDate}
                    >
                        Generar Reporte
                    </button>
                </div>
            </form>
        </section>
    );
};

export default InspectionDateRangeReport;
